import React, { useCallback, useEffect, useState } from 'react'
import { triggerSentryError } from '../../../../shared/helpers'
import tts from '../../lib/TTS'
import Add from '../Icons/Add/Add'
import {
  addToFavorites,
  getAllFavorites,
  getFavorites,
  removeFromFavorites,
} from '../../actions/contentActions'
import { hasNoNullishValues } from '../../lib/helpers'
import { useAppSelector } from '../../hooks/useStore'
import { RootState } from '../../reducers/types/reducerTypes'
import Checkmark from '../Icons/Checkmark/Checkmark'
import FocusableButtonTS from '../FocusableButton/FocusableButtonTS'
import PlayButton from '../Icons/PlayButton/PlayButton'
import { useNavigate } from 'react-router'
import { useErrorModal } from '../../screens/VideoDetailScreen/context/ErrorModalProvider'
import { ActionButtonsProps } from './types'

const MY_LIST = 'My List'
const MY_LIST_BUTTON_KEY = 'MY_LIST_BUTTON_KEY'
const PLAY_BUTTON_KEY = 'PLAY_BUTTON_KEY'
const RESUME = 'Resume'
const START_WATCHING = 'Start Watching'

const ActionButtons = ({ onArrowPress }: ActionButtonsProps) => {
  const { setErrorModalVisible, setErrorModalNotVisible, setErrorModalProps } =
    useErrorModal()
  const [isFavorite, setIsFavorite] = useState(false)
  const navigate = useNavigate()
  const { favorites, favoritesFullyLoaded } = useAppSelector(
    (state: RootState) => state.personalization
  )
  const showDetail = useAppSelector((state: RootState) => state.showDetail)
  const {
    contentHasSecondsWatched,
    contentEpisode,
    contentResourceType,
    contentSeason,
    contentUrl,
  } = showDetail.continueWatchingData
  const isContinueWatchingDataAvailable = hasNoNullishValues(
    showDetail.continueWatchingData
  )
  const UID = useAppSelector((state: RootState) => state.device.UID)

  useEffect(() => {
    checkFavorites()
  }, [])

  const isFavoriteFn = useCallback(
    (favoritesList: any[], showId: string) =>
      !!favoritesList?.find((y) => y.cid === showId),
    []
  )

  useEffect(() => {
    if (favorites !== null && favorites !== 'error') {
      const showId = showDetail.pageDetails?.cid
      if (showId && !isFavorite) {
        const favorite = isFavoriteFn(favorites as any[], showId)
        setIsFavorite(favorite)
      }
    }
  }, [favorites, showDetail.pageDetails?.cid, isFavoriteFn, isFavorite])

  const getUserFavoriteContent = async () => {
    const showId = showDetail.pageDetails?.cid
    if (favorites === null) {
      try {
        await getFavorites(UID)
      } catch (err) {
        triggerSentryError('getFavorites', 'ActionButtons.tsx', {
          errorMessage: err.message ?? 'Undefined error message (default)',
          errorDescription: `Error ${
            err.status || '(unknown)'
          } getting favorites`,
          errorLocation: 'ActionButtons.tsx - getUserFavoriteContent',
        })
      }
    } else {
      const favorite = isFavoriteFn(favorites as any[], showId)
      if (favorite || favoritesFullyLoaded) {
        setIsFavorite(favorite)
      } else {
        getAllFavorites(UID)
      }
    }
  }

  const checkFavorites = () => {
    const showId = showDetail.pageDetails?.cid
    if (showId !== null && favorites !== 'error') {
      getUserFavoriteContent()
    }
  }

  const handleAddToFavorites = async () => {
    const { pageDetails } = showDetail

    addToFavorites(UID, pageDetails)
      .then(() => {
        tts.addTextToRead('Added to My List')
        setIsFavorite(true)
      })
      .catch((err) => {
        toggleErrorModal(
          'There was an error adding this show to your Favorites.'
        )
        triggerSentryError('addToFavorites', 'ActionButtons.tsx', {
          errorMessage: err.message ?? 'Undefined error message (default)',
          errorDescription: `Error ${err.status || '(unknown)'} adding show ${
            pageDetails.title
          } to favorites`,
          errorLocation: 'ActionButtons.tsx - handleAddToFavorites',
        })
      })
  }

  const handleRemoveFromFavorites = async () => {
    const { pageDetails } = showDetail

    removeFromFavorites(UID, pageDetails.cid)
      .then(() => {
        tts.addTextToRead('Removed from My List')
        setIsFavorite(false)
      })
      .catch((err) => {
        toggleErrorModal(
          'There was an error removing this show from your Favorites.'
        )

        triggerSentryError('removeFromFavorites', 'ActionButtons.tsx', {
          errorMessage: err.message ?? 'Undefined error message (default)',
          errorDescription: `Error ${err.status || '(unknown)'} removing show ${
            pageDetails.title
          } from favorites`,
          errorLocation: 'ActionButtons.tsx - handleRemoveFromFavorites',
        })
      })
  }

  const toggleFavorites = () => {
    isFavorite ? handleRemoveFromFavorites() : handleAddToFavorites()
  }

  const toggleErrorModal = (title: string) => {
    setErrorModalVisible()
    setErrorModalProps({
      title,
      buttons: [
        {
          label: 'Go Back',
          onEnterPress: () => setErrorModalNotVisible(),
          focusKey: 'goBackButton',
        },
      ],
      hideStationLogo: false,
      description: '',
      error: null,
    })
  }

  const onMyListFocus = () => {
    tts.stopRead()
    tts.addTextToRead(
      `Button, ${isFavorite ? 'Remove from ' : 'Add to '} My List`
    )
  }

  const onResumeFocus = () => {
    tts.stopRead()
    tts.addTextToRead(
      `Button, ${
        contentHasSecondsWatched ? RESUME : START_WATCHING
      } Season ${contentSeason} Episode ${contentEpisode}`
    )
  }

  const handleArrowPress = (direction: string) => {
    if (!isContinueWatchingDataAvailable) {
      onArrowPress(direction)
    }
    return true
  }

  const handleNavigateToVideoDetails = () => {
    navigate(`/video-details/${encodeURIComponent(contentUrl)}`)
  }

  const season = contentSeason ? contentSeason : ''
  const episode = contentEpisode ? contentEpisode : ''
  const buttonLabel = `${contentHasSecondsWatched ? RESUME : START_WATCHING} ${
    season && episode
      ? `S${season} E${episode}`
      : `${contentResourceType
          .charAt(0)
          .toUpperCase()}${contentResourceType.slice(1)}`
  }`

  return (
    <>
      <FocusableButtonTS
        className="button"
        focusKey={MY_LIST_BUTTON_KEY}
        onEnterPress={toggleFavorites}
        onFocus={onMyListFocus}
        onArrowPress={handleArrowPress}
        stealFocus={!isContinueWatchingDataAvailable}
      >
        {!isFavorite ? <Add /> : <Checkmark />}
        <span className="action-label">{MY_LIST}</span>
      </FocusableButtonTS>
      {isContinueWatchingDataAvailable && (
        <FocusableButtonTS
          focusKey={PLAY_BUTTON_KEY}
          onEnterPress={handleNavigateToVideoDetails}
          onArrowPress={onArrowPress}
          onFocus={onResumeFocus}
          stealFocus={true}
        >
          <>
            <PlayButton />
            <span className="action-label">{buttonLabel}</span>
          </>
        </FocusableButtonTS>
      )}
    </>
  )
}

export default ActionButtons
